import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { type MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpacePath } from '../../../../hooks/useSpacePath';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { PendingSubTopicWithPreview } from '../../../../types';
import { RECENT_ACTIVITIES_LIMIT, useGetRecentActivities } from '../../api/useGetRecentActivities';
import { useGetSubTopicIncidents } from '../../api/useGetSubTopicIncidents';
import { isPlainLeftClick } from '../../../../utils/isPlainLeftClick';
import { formatQuoteAmount } from '../../utils/formatQuoteAmount';

export const MAX_TASKS = 2;
export const MAX_REMINDERS = 2;
export const MAX_PENDING_ITEMS = 2;
export const MAX_PENDING_QUOTES = 2;
export const MAX_ACTIVITIES = RECENT_ACTIVITIES_LIMIT;

type Props = {
  subTopic: PendingSubTopicWithPreview;
};

type PreviewQuote = PendingSubTopicWithPreview['preview_pending_quotes'][number];

export function toPendingQuotePreviewItem(quote: PreviewQuote) {
  return {
    id: quote.id,
    label: quote.provider_name ?? quote.title ?? 'Devis',
    meta: quote.amount == null ? undefined : `(${formatQuoteAmount(Number(quote.amount))})`,
  };
}

export function usePendingSubTopicCard(props: Props) {
  const spacePath = useSpacePath();
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const targetUrl = spacePath(`/topics/${props.subTopic.id}`);

  // Only the latest activities are shown on the card: no need to load the whole timeline.
  const {
    data: recentActivitiesData,
    isLoading: activitiesLoading,
  } = useGetRecentActivities({ subTopicId: props.subTopic.id }, MAX_ACTIVITIES);

  const { data: incidents } = useGetSubTopicIncidents(props.subTopic.id);

  const pendingTasks = props.subTopic.preview_tasks ?? [];
  const pendingItems = props.subTopic.preview_pending_items ?? [];
  const reminders = props.subTopic.preview_reminders ?? [];
  const pendingQuotes = props.subTopic.preview_pending_quotes ?? [];

  const reminderItems = useMemo(() => {
    const now = new Date();
    return reminders.map((reminder) => {
      const dueDate = new Date(reminder.due_date);
      return {
        id: reminder.id,
        label: reminder.title,
        meta: `(${format(dueDate, 'd MMM yyyy', { locale: fr })})`,
        isOverdue: dueDate < now,
      };
    });
  }, [reminders]);

  const taskItems = useMemo(() => {
    return pendingTasks.map((task) => ({ id: task.id, label: task.title }));
  }, [pendingTasks]);

  const pendingItemItems = useMemo(() => {
    return pendingItems.map((item) => ({
      id: item.id,
      label: item.title,
      meta: item.provider_name ? `(${item.provider_name})` : undefined,
    }));
  }, [pendingItems]);

  const pendingQuoteItems = useMemo(() => pendingQuotes.map(toPendingQuotePreviewItem), [pendingQuotes]);

  function handleTitleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    navigate(targetUrl);
  }

  const hasOngoingIncident = useMemo(() => {
    return Boolean(
      incidents?.some((i) => i.status === 'in_progress' || (i.status as string) === 'open')
    );
  }, [incidents]);

  return {
    targetUrl,
    handleTitleClick,
    isAdmin,
    reminderItems,
    taskItems,
    pendingItemItems,
    pendingQuoteItems,
    activitiesLoading,
    recentActivities: recentActivitiesData?.items ?? [],
    totalActivitiesCount: recentActivitiesData?.total ?? 0,
    hasOngoingIncident,
  };
}
